import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useDataProvider, useNotify } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { UserEntityPermissionsDialogForm } from 'shared/widgets/UserEntityPermissionsDialog/components/UserEntityPermissionsForm/UserEntityPermissionsDialogForm';
import { USER_ENTITY_PERMISSIONS_RESOURCES } from 'shared/widgets/UserEntityPermissionsDialog/constants/userEntityPermissionsNames';
import {
  UserEntityPermission,
  UserEntityPermissionsNames,
} from 'types/embedded/userEntityPermission';
import { formatInputToTimestamp } from 'utils/date';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  recordName: string;
  recordId: string;
  entityName: UserEntityPermissionsNames;
};

export type UserEntityPermissionFormValues = {
  permissions: UserEntityPermission[];
};

export const UserEntityPermissionsDialogCreate: React.FC<Props> = ({
  open,
  onClose,
  title,
  recordName,
  recordId,
  entityName,
}) => {
  const translate = useTypedTranslate();

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const form = useForm({
    defaultValues: {
      permissions: [
        {
          user: null,
          expiredAt: null,
          entity: entityName,
          rowId: Number(recordId),
        },
      ],
    },
  });

  const onSubmit = async (formValues: UserEntityPermissionFormValues) => {
    try {
      await Promise.all(
        formValues?.permissions.map((formValue) =>
          dataProvider.create('user_entity_permissions', {
            data: {
              ...formValue,
              expiredAt: formatInputToTimestamp(formValue.expiredAt, { unit: 'seconds' }),
            },
          }),
        ),
      );
      await queryClient.invalidateQueries({
        queryKey: [USER_ENTITY_PERMISSIONS_RESOURCES[entityName]],
      });
      onClose();
      notify(translate('app.merchants.allAccessSettings'), { type: 'success' });
    } catch (error) {
      notify((error as Error)?.message || translate('app.common.serverError'), { type: 'error' });
    }
  };

  return (
    <Dialog maxWidth="sm" onClose={onClose} open={open}>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title={title}
            titleSize="smallBold"
          />
          <CardContent>
            <Alert severity="warning" sx={{ marginBottom: '24px' }}>
              {translate('app.merchants.afterAccessSettingsConfirmation', { recordName })}
            </Alert>
            <UserEntityPermissionsDialogForm
              entityName={entityName}
              recordId={recordId}
              recordName={recordName}
            />
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="cancel"
                label={translate('app.buttons.cancel')}
                onClick={onClose}
                variant="outlined"
              />,
              <Button
                key="create"
                label={translate('app.buttons.save')}
                type="submit"
                variant="contained"
              />,
            ]}
          />
        </form>
      </FormProvider>
    </Dialog>
  );
};
